import React, { useRef, useEffect, useState } from "react";
import { StarIcon, StorePinIcon, CheckIcon, SearchIcon, ClearIcon } from "./StoreSelectorIcons.tsx";

interface Store {
  _id: string;
  name: string;
  city?: string;
}

interface Props {
  stores: Store[];
  selectedId: string | null;
  onSelectAll: () => void;
  onSelectStore: (store: Store) => void;
}

export default function StoreSelectorPanel({ stores, selectedId, onSelectAll, onSelectStore }: Props) {
  const [search, setSearch] = useState("");
  const searchRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  const isAllSelected = selectedId === null;
  const storeCount = stores.length;

  const filtered = search.trim()
    ? stores.filter((s) =>
        `${s.name} ${s.city ?? ""}`.toLowerCase().includes(search.toLowerCase())
      )
    : stores;

  // Focus search on mount
  useEffect(() => {
    setTimeout(() => searchRef.current?.focus(), 60);
  }, []);

  // Scroll active item into view
  useEffect(() => {
    const active = listRef.current?.querySelector<HTMLElement>("[data-active='true']");
    active?.scrollIntoView({ block: "nearest" });
  }, []);

  return (
    <div
      className="absolute right-0 top-[calc(100%+8px)] z-50 w-72 rounded-lg border border-[rgb(var(--border-secondary))] bg-surface-1 shadow-card-hover overflow-hidden"
      style={{ animation: "scopeDropIn 0.16s cubic-bezier(.4,0,.2,1)" }}
    >
      {/* ── Header ── */}
      <div className="px-3 pt-3 pb-2.5 border-b border-[rgb(var(--border-primary))] bg-surface-2">
        <div className="flex items-center justify-between mb-2.5">
          <div className="flex items-center gap-1.5">
            <span className="text-gold-2 flex"><StarIcon size={11} /></span>
            <span className="text-[10px] font-bold tracking-[0.1em] uppercase text-gold-2">
              Select Scope
            </span>
          </div>
          <span className="text-[10px] font-semibold text-text-muted bg-surface-3 border border-[rgb(var(--border-primary))] px-2 py-0.5 rounded-full">
            {storeCount} stores
          </span>
        </div>

        {/* Search */}
        <div className="flex items-center gap-2 px-2.5 py-1.5 rounded-md border border-[rgb(var(--border-secondary))] bg-surface-1">
          <span className="text-text-muted flex shrink-0"><SearchIcon /></span>
          <input
            ref={searchRef}
            type="text"
            placeholder="Search stores…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="flex-1 bg-transparent border-none outline-none text-[12px] text-text-primary placeholder:text-text-muted font-sans"
          />
          {search && (
            <button
              type="button"
              onClick={() => setSearch("")}
              className="text-text-muted hover:text-text-primary flex shrink-0 transition-colors"
            >
              <ClearIcon />
            </button>
          )}
        </div>
      </div>

      {/* ── List ── */}
      <div
        ref={listRef}
        role="listbox"
        className="max-h-56 overflow-y-auto p-1.5 space-y-0.5"
        style={{ scrollbarWidth: "thin" }}
      >
        {/* All Stores */}
        {!search && (
          <StoreOption
            icon={<span className="text-gold-2 flex"><StarIcon size={12} /></span>}
            label="All Stores Combined"
            sublabel={`${storeCount} location${storeCount !== 1 ? "s" : ""}`}
            isSelected={isAllSelected}
            isActive={isAllSelected}
            onClick={onSelectAll}
          />
        )}

        {/* Divider */}
        {!search && filtered.length > 0 && (
          <div className="flex items-center gap-2 px-1 py-1">
            <div className="flex-1 h-px bg-[rgb(var(--border-primary))]" />
            <span className="text-[9px] font-bold tracking-[0.1em] uppercase text-text-muted">
              Locations
            </span>
            <div className="flex-1 h-px bg-[rgb(var(--border-primary))]" />
          </div>
        )}

        {/* Individual stores */}
        {filtered.length === 0 ? (
          <div className="py-5 text-center">
            <p className="text-[12px] text-text-muted font-medium">No stores match "{search}"</p>
          </div>
        ) : (
          filtered.map((item) => (
            <StoreOption
              key={item._id}
              icon={<span className={`flex ${selectedId === item._id ? "text-gold-2" : "text-text-muted"}`}><StorePinIcon /></span>}
              label={item.name}
              sublabel={item.city}
              isSelected={selectedId === item._id}
              isActive={selectedId === item._id}
              onClick={() => onSelectStore(item)}
            />
          ))
        )}
      </div>

      {/* ── Footer ── */}
      <div className="flex items-center justify-between px-3 py-2 border-t border-[rgb(var(--border-primary))] bg-surface-2">
        <span className="text-[10.5px] text-text-muted font-medium">
          {isAllSelected ? `All ${storeCount} stores in scope` : `1 of ${storeCount} selected`}
        </span>
        {!isAllSelected && (
          <button
            type="button"
            onClick={onSelectAll}
            className="text-[10.5px] font-semibold text-gold-2 hover:text-gold-3 hover:bg-gold-1/10 px-2 py-0.5 rounded transition-colors"
          >
            Reset
          </button>
        )}
      </div>

      <style>{`
        @keyframes scopeDropIn {
          from { opacity: 0; transform: translateY(-5px) scale(0.98); }
          to   { opacity: 1; transform: translateY(0) scale(1); }
        }
      `}</style>
    </div>
  );
}

// ── Reusable option row ────────────────────────────────────────────────────────

function StoreOption({
  icon,
  label,
  sublabel,
  isSelected,
  isActive,
  onClick,
}: {
  icon: React.ReactNode;
  label: string;
  sublabel?: string;
  isSelected: boolean;
  isActive: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      role="option"
      aria-selected={isSelected}
      data-active={isActive}
      onClick={onClick}
      className={[
        "w-full flex items-center gap-2.5 px-2.5 py-2 rounded-md text-left transition-all duration-150",
        "border",
        isSelected
          ? "bg-gold-1/10 border-gold-2/25 text-gold-2"
          : "border-transparent hover:bg-surface-2 text-text-primary",
      ].join(" ")}
    >
      {/* Icon badge */}
      <span className={[
        "w-7 h-7 rounded-md flex items-center justify-center shrink-0 transition-colors",
        isSelected ? "bg-gold-1/15" : "bg-surface-3",
      ].join(" ")}>
        {icon}
      </span>

      {/* Text */}
      <span className="flex-1 min-w-0">
        <span className={[
          "block text-[12.5px] truncate tracking-tight",
          isSelected ? "font-semibold text-gold-2" : "font-medium text-text-primary",
        ].join(" ")}>
          {label}
        </span>
        {sublabel && (
          <span className="block text-[10.5px] text-text-muted truncate font-normal">
            {sublabel}
          </span>
        )}
      </span>

      {/* Check */}
      {isSelected && (
        <span className="w-4.5 h-4.5 rounded-full bg-gold-2 flex items-center justify-center text-white shrink-0">
          <CheckIcon />
        </span>
      )}
    </button>
  );
}
